// Servicio para Text-to-Speech usando ElevenLabs API

const ELEVENLABS_API_KEY = process.env.REACT_APP_ELEVENLABS_API_KEY || '';
const ELEVENLABS_API_URL = 'https://api.elevenlabs.io/v1';

export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category: string;
  labels?: {
    verified_languages?: Array<{
      language: string;
      locale: string;
    }>;
  };
}

export interface TTSMethod {
  type: 'native' | 'elevenlabs';
  voiceId?: string; // Para ElevenLabs
}

let availableVoices: ElevenLabsVoice[] | null = null;
let voicesPromise: Promise<ElevenLabsVoice[]> | null = null;

/**
 * Obtener lista de voces disponibles en ElevenLabs
 */
export async function getAvailableVoices(): Promise<ElevenLabsVoice[]> {
  if (!ELEVENLABS_API_KEY) {
    console.warn('ElevenLabs API key no configurada');
    return [];
  }

  console.log('[ElevenLabs] API Key configurada:', ELEVENLABS_API_KEY ? 'Sí' : 'No');
  console.log('[ElevenLabs] Primeros caracteres:', ELEVENLABS_API_KEY.substring(0, 10) + '...');

  if (availableVoices) {
    return availableVoices;
  }

  // Si ya hay una petición en curso, reutilizarla (evita llamadas duplicadas)
  if (voicesPromise) {
    return voicesPromise;
  }

  try {
    voicesPromise = (async () => {
      const response = await fetch(`${ELEVENLABS_API_URL}/voices`, {
        headers: {
          'xi-api-key': ELEVENLABS_API_KEY,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ElevenLabs] Error response:', response.status, errorText);
        throw new Error(`Error fetching voices: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      availableVoices = (data.voices || []) as ElevenLabsVoice[];
      console.log('[ElevenLabs] Voces cargadas:', availableVoices.length);
      return availableVoices;
    })();

    return await voicesPromise;
  } catch (error) {
    console.error('Error fetching ElevenLabs voices:', error);
    voicesPromise = null; // Limpiar para reintentar en caso de error
    return [];
  }
}

/**
 * Obtener voces en español
 */
export async function getSpanishVoices(): Promise<ElevenLabsVoice[]> {
  const allVoices = await getAvailableVoices();

  const spanishVoices = allVoices.filter(voice => {
    const hasSpanish = voice.labels?.verified_languages?.some(
      (lang: any) => lang.language === 'es' || lang.locale?.startsWith('es')
    );

    const matches = hasSpanish ||
      voice.name.toLowerCase().includes('spanish') ||
      voice.name.toLowerCase().includes('español');

    if (matches) {
      console.log('[ElevenLabs] Voz española:', voice.name, 'langs:', voice.labels?.verified_languages);
    }

    return matches;
  });

  console.log('[ElevenLabs] Total españolas:', spanishVoices.length);
  return spanishVoices;
}

/**
 * Convertir texto a audio usando ElevenLabs
 */
export async function textToSpeech(
  text: string,
  voiceId: string,
  onProgress?: (progress: number) => void
): Promise<{ audioUrl: string; blob: Blob }> {
  if (!ELEVENLABS_API_KEY) {
    throw new Error('ElevenLabs API key no configurada');
  }

  const response = await fetch(`${ELEVENLABS_API_URL}/text-to-speech/${voiceId}`, {
    method: 'POST',
    headers: {
      'xi-api-key': ELEVENLABS_API_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text,
      model_id: 'eleven_multilingual_v2',
      voice_settings: {
        stability: 0.78,
        similarity_boost: 0.5,
        style: 0.06, // exageración
      },
      language_id: 'es', // Español
    }),
  });

  if (!response.ok) {
    throw new Error(`Error en TTS: ${response.status}`);
  }

  const blob = await response.blob();
  const audioUrl = URL.createObjectURL(blob);

  return { audioUrl, blob };
}

/**
 * Verificar si ElevenLabs está configurado
 */
export function isElevenLabsConfigured(): boolean {
  return !!ELEVENLABS_API_KEY;
}

/**
 * Gestor de streaming TTS con WebSocket
 * Maneja la conexión WebSocket y reproducción de audio en tiempo real
 */
export class StreamingTTSManager {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private queueSource: AudioBufferSourceNode | null = null;
  private audioQueue: AudioBuffer[] = [];
  private isPlaying = false;
  private onAudioPlayed: (() => void) | null | undefined = null;

  /**
   * Iniciar conexión WebSocket y configurar
   */
  connect(voiceId: string, onAudioPlayed?: () => void): Promise<void> {
    if (!ELEVENLABS_API_KEY) {
      throw new Error('ElevenLabs API key no configurada');
    }

    this.onAudioPlayed = onAudioPlayed;

    return new Promise((resolve, reject) => {
      const wsUrl = `wss://api.elevenlabs.io/v1/text-to-speech/${voiceId}/stream-input?model_id=eleven_multilingual_v2`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[StreamingTTS] WebSocket conectado');

        // Enviar configuración inicial
        this.ws!.send(JSON.stringify({
          text: ' ',
          voice_settings: {
            stability: 0.78,
            similarity_boost: 0.5,
            style: 0.06,
          },
          xi_api_key: ELEVENLABS_API_KEY,
        }));

        // Iniciar AudioContext
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        resolve();
      };

      this.ws.onerror = (error) => {
        console.error('[StreamingTTS] WebSocket error:', error);
        reject(error);
      };

      this.ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        if (data.audio) {
          // Decodificar audio base64
          const audioData = Uint8Array.from(atob(data.audio), c => c.charCodeAt(0));
          const audioBuffer = await this.audioContext!.decodeAudioData(audioData.buffer);
          this.audioQueue.push(audioBuffer);

          if (!this.isPlaying) {
            this.playNext();
          }
        }

        if (data.isFinal) {
          console.log('[StreamingTTS] Stream finalizado');
        }
      };

      this.ws.onclose = () => {
        console.log('[StreamingTTS] WebSocket cerrado');
      };
    });
  }

  /**
   * Enviar texto para convertir a audio
   */
  sendText(text: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        text: text + ' ', // Espacio para indicar continuación
        xi_api_key: ELEVENLABS_API_KEY,
      }));
    }
  }

  /**
   * Indicar que no hay más texto
   */
  finalize(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        text: '',
        try_trigger_generation: true,
        xi_api_key: ELEVENLABS_API_KEY,
      }));
    }
  }

  /**
   * Reproducir el siguiente audio en cola
   */
  private playNext(): void {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      this.onAudioPlayed?.();
      return;
    }

    this.isPlaying = true;
    const buffer = this.audioQueue.shift()!;

    this.queueSource = this.audioContext!.createBufferSource();
    this.queueSource.buffer = buffer;
    this.queueSource.connect(this.audioContext!.destination!);

    this.queueSource.onended = () => {
      this.playNext();
    };

    this.queueSource.start();
  }

  /**
   * Cancelar todo y cerrar conexión
   */
  cancel(): void {
    // Detener audio actual
    if (this.queueSource) {
      try {
        this.queueSource.stop();
      } catch (e) {
        // Ya estaba detenido
      }
      this.queueSource = null;
    }

    // Limpiar cola
    this.audioQueue = [];
    this.isPlaying = false;

    // Cerrar WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Cerrar AudioContext
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Verificar si está activo
   */
  isActive(): boolean {
    return (this.ws?.readyState === WebSocket.OPEN) || this.audioQueue.length > 0 || this.isPlaying;
  }
}

// Instancia global del streaming TTS
export const streamingTTS = new StreamingTTSManager();
